/**
 * Utilitaires de validation UUID pour éviter les race conditions Firebase UID vs Supabase UUID
 * 
 * Ce fichier contient les fonctions de validation pour s'assurer que les IDs utilisés
 * dans les requêtes Supabase sont des UUID valides au format PostgreSQL.
 */

/**
 * Regex stricte pour validation UUID v4 compatible PostgreSQL
 * Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 * où x est un hexadécimal [0-9a-f] et y est [89ab]
 */
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Regex plus permissive pour UUID général (v1, v2, v3, v4, v5)
 * Format: xxxxxxxx-xxxx-Mxxx-Nxxx-xxxxxxxxxxxx
 */
const UUID_GENERAL_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Pattern pour détecter Firebase UID (caractères alphanumériques sans tirets)
 * Firebase UIDs sont généralement 28 caractères, base64url encodé
 */
const FIREBASE_UID_PATTERN = /^[A-Za-z0-9_-]{20,30}$/;

/**
 * Interface pour les résultats de validation
 */
export interface UUIDValidationResult {
  isValid: boolean;
  type: 'uuid-v4' | 'uuid-general' | 'firebase-uid' | 'invalid' | 'empty';
  reason?: string;
}

/**
 * Valide si une chaîne est un UUID v4 valide
 * 
 * @param id - ID à valider
 * @returns true si UUID v4 valide
 */
export function isValidUUIDv4(id: string | null | undefined): boolean {
  if (!id || typeof id !== 'string') return false;
  return UUID_V4_REGEX.test(id);
}

/**
 * Valide si une chaîne est un UUID général valide (v1-v5)
 * 
 * @param id - ID à valider 
 * @returns true si UUID valide
 */
export function isValidUUID(id: string | null | undefined): boolean {
  if (!id || typeof id !== 'string') return false;
  return UUID_GENERAL_REGEX.test(id);
}

/**
 * Valide si une chaîne est un UUID Supabase valide (pour éviter Firebase UID)
 * Utilise UUID général mais avec vérifications supplémentaires
 * 
 * @param id - ID à valider
 * @returns true si UUID Supabase valide
 */
export function isValidSupabaseUUID(id: string | null | undefined): id is string {
  if (!id || typeof id !== 'string') return false;
  
  // Doit avoir des tirets (Firebase UID n'en a pas)
  if (!id.includes('-')) return false;
  
  // Doit être un UUID valide
  if (!isValidUUID(id)) return false;
  
  // Ne doit pas ressembler à un Firebase UID
  if (FIREBASE_UID_PATTERN.test(id.replace(/-/g, ''))) return false;
  
  return true;
}

/**
 * Détecte si une chaîne ressemble à un Firebase UID
 * 
 * @param id - ID à tester
 * @returns true si ressemble à Firebase UID
 */
export function isFirebaseUID(id: string | null | undefined): boolean {
  if (!id || typeof id !== 'string') return false;
  
  // Firebase UID n'a pas de tirets
  if (id.includes('-')) return false;
  
  // Correspond au pattern Firebase
  return FIREBASE_UID_PATTERN.test(id);
}

/**
 * Validation complète avec détails
 * 
 * @param id - ID à valider
 * @returns Résultat détaillé de validation
 */
export function validateUUID(id: string | null | undefined): UUIDValidationResult {
  if (!id || typeof id !== 'string' || id.trim() === '') {
    return { isValid: false, type: 'empty', reason: 'ID is null, undefined, or empty' };
  }
  
  const trimmedId = id.trim();
  
  // Test Firebase UID en premier
  if (isFirebaseUID(trimmedId)) {
    return { 
      isValid: false, 
      type: 'firebase-uid', 
      reason: `Firebase UID detected: ${trimmedId.substring(0, 10)}...` 
    };
  }
  
  // Test UUID v4
  if (isValidUUIDv4(trimmedId)) {
    return { isValid: true, type: 'uuid-v4' };
  }
  
  // Test UUID général
  if (isValidUUID(trimmedId)) {
    return { isValid: true, type: 'uuid-general' };
  }
  
  // Format invalide
  return { 
    isValid: false, 
    type: 'invalid', 
    reason: `Invalid UUID format: ${trimmedId.substring(0, 20)}...` 
  };
}

/**
 * Logger les problèmes de validation UUID avec contexte
 * 
 * @param id - ID problématique
 * @param context - Contexte où l'erreur s'est produite
 * @param validation - Résultat de validation
 */
export function logUUIDMismatch(
  id: string | null | undefined, 
  context: string, 
  validation?: UUIDValidationResult
): void {
  const result = validation || validateUUID(id);
  
  const logData = {
    context,
    id: id ? `${id.substring(0, 15)}...` : 'null/undefined',
    fullId: id,
    validationType: result.type,
    isValid: result.isValid,
    reason: result.reason
  };
  
  if (result.type === 'firebase-uid') {
    console.error('[UUID Mismatch] 🚨 Firebase UID passed instead of Supabase UUID:', logData);
  } else if (!result.isValid) {
    console.warn('[UUID Validation] ⚠️ Invalid UUID format:', logData);
  } else {
    console.log('[UUID Validation] ✅ Valid UUID:', logData);
  }
}

/**
 * Attend qu'un UUID valide soit disponible (helper pour éviter race conditions)
 * 
 * @param getter - Fonction qui retourne l'UUID à attendre
 * @param timeout - Timeout en millisecondes (défaut: 5000)
 * @param interval - Intervalle de vérification en millisecondes (défaut: 100)
 * @returns Promise qui résout avec UUID valide ou rejette au timeout
 */
export function waitForValidUUID(
  getter: () => string | null | undefined,
  timeout: number = 5000,
  interval: number = 100
): Promise<string> {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    
    const checkUUID = () => {
      const id = getter();
      const validation = validateUUID(id);
      
      if (validation.isValid && id) {
        console.log('[UUID Wait] ✅ Valid UUID obtained:', { id: id.substring(0, 15) + '...', timeTaken: Date.now() - startTime });
        resolve(id);
        return;
      }
      
      if (Date.now() - startTime > timeout) {
        const error = `Timeout waiting for valid UUID after ${timeout}ms. Last validation: ${JSON.stringify(validation)}`;
        console.error('[UUID Wait] ❌ Timeout:', error);
        reject(new Error(error));
        return;
      }
      
      // Log périodique pendant l'attente
      if ((Date.now() - startTime) % 1000 < interval) {
        console.log('[UUID Wait] ⏳ Waiting for valid UUID...', { 
          currentId: id ? `${id.substring(0, 10)}...` : 'null',
          validation: validation.type,
          elapsed: Date.now() - startTime 
        });
      }
      
      setTimeout(checkUUID, interval);
    };
    
    checkUUID();
  });
}

/**
 * Conversion sécurisée Firebase UID -> Supabase UUID avec validation
 * À utiliser quand on a besoin de convertir un Firebase UID en UUID Supabase
 * 
 * @param firebaseUID - Firebase UID à convertir
 * @param converter - Fonction de conversion (ex: getInternalUserId)
 * @returns Promise avec UUID Supabase valide
 */
export async function safeFirebaseToSupabaseConversion(
  firebaseUID: string,
  converter: (uid: string) => Promise<string>
): Promise<string> {
  // Valider l'input
  if (!isFirebaseUID(firebaseUID)) {
    throw new Error(`Invalid Firebase UID format: ${firebaseUID.substring(0, 15)}...`);
  }
  
  console.log('[UUID Conversion] 🔄 Converting Firebase UID to Supabase UUID:', firebaseUID.substring(0, 10) + '...');
  
  try {
    const supabaseUUID = await converter(firebaseUID);
    const validation = validateUUID(supabaseUUID);
    
    if (!validation.isValid) {
      logUUIDMismatch(supabaseUUID, 'safeFirebaseToSupabaseConversion', validation);
      throw new Error(`Conversion returned invalid UUID: ${validation.reason}`);
    }
    
    console.log('[UUID Conversion] ✅ Successfully converted to Supabase UUID:', supabaseUUID.substring(0, 15) + '...');
    return supabaseUUID;
    
  } catch (error) {
    console.error('[UUID Conversion] ❌ Conversion failed:', {
      firebaseUID: firebaseUID.substring(0, 10) + '...',
      error: error instanceof Error ? error.message : 'Unknown error'
    });
    throw error;
  }
}